/**
 * E013-S007: Simplified Notion Import Preview
 *
 * Clean modal following AFFiNE design patterns.
 * User picks import mode and target doc.
 */

import { Button, Input, RadioGroup } from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useI18n } from '@affine/i18n';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { formatImportCount } from '../preview-i18n';
import * as styles from './styles.css';
import type { ImportPreview } from './types';
import { parseNotionZip } from './zip-parser';

export type ImportMode = 'standard' | 'edgeless';

export interface NotionImportConfig {
  importMode: ImportMode;
  targetType: 'new' | 'existing';
  newDocName: string;
  existingDocId: string | null;
}

interface NotionImportPreviewProps {
  file: File;
  onImport: (config: NotionImportConfig) => void;
  onCancel: () => void;
  importing?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extractCleanName(filename: string): string {
  let name = filename.replace(/\.zip$/i, '');
  name = name.replace(/\s+[a-f0-9]{32}$/i, '');
  try {
    name = decodeURIComponent(name);
  } catch {
    // Keep as-is
  }
  return name || filename;
}

export const NotionImportPreview: FC<NotionImportPreviewProps> = ({
  file,
  onImport,
  onCancel,
  importing = false,
}) => {
  const t = useI18n();
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Always use edgeless mode for Enhanced import
  const importMode: ImportMode = 'edgeless';
  const [targetType, setTargetType] = useState<'new' | 'existing'>('new');
  const [newDocName, setNewDocName] = useState('');

  useEffect(() => {
    let cancelled = false;

    const parse = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await parseNotionZip(file);
        if (!cancelled) {
          setPreview(result);
          setNewDocName(extractCleanName(file.name));
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : t['com.streamlain.import.preview.notion.failed-parse']()
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    parse().catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [file, t]);

  const handleImport = useCallback(() => {
    onImport({
      importMode,
      targetType,
      newDocName: newDocName || extractCleanName(file.name),
      existingDocId: null,
    });
  }, [importMode, targetType, newDocName, file.name, onImport]);

  // Mode is always 'edgeless' for Enhanced import
  // Standard import uses the separate "Notion Import" button

  const targetOptions = useMemo(
    () => [
      {
        value: 'new',
        label: t['com.streamlain.import.preview.notion.target.new'](),
      },
      {
        value: 'existing',
        label: t['com.streamlain.import.preview.notion.target.existing-soon'](),
        disabled: true,
      },
    ],
    [t]
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.title}>
            {t['com.streamlain.import.preview.notion.title']()}
          </div>
        </div>
        <div className={styles.loadingState}>
          {t['com.streamlain.import.preview.notion.analyzing']()}
        </div>
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.title}>
            {t['com.streamlain.import.preview.notion.title']()}
          </div>
        </div>
        <div className={styles.errorState}>
          {error || t['com.streamlain.import.preview.notion.failed-parse']()}
        </div>
        <div className={styles.footer}>
          <Button onClick={onCancel}>{t['Cancel']()}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          {t['com.streamlain.import.preview.notion.enhanced-title']()}
        </div>
        <div className={styles.subtitle}>
          {t['com.streamlain.import.preview.notion.enhanced-subtitle']()}
        </div>
      </div>

      <div className={styles.content}>
        <SettingWrapper title={t['com.streamlain.import.preview.file']()}>
          <div className={styles.fileCard}>
            <div className={styles.fileName}>{extractCleanName(file.name)}</div>
            <div className={styles.fileMeta}>
              {formatFileSize(file.size)}
              {preview.totalDatabases > 0 &&
                ` · ${formatImportCount(t, 'database', preview.totalDatabases)}`}
              {preview.totalRows > 0 &&
                ` · ${formatImportCount(t, 'row', preview.totalRows)}`}
            </div>
          </div>
        </SettingWrapper>

        <SettingWrapper title={t['com.streamlain.import.preview.import-to']()}>
          <SettingRow
            name={t['com.streamlain.import.preview.notion.target']()}
            spreadCol={false}
          >
            <RadioGroup
              items={targetOptions}
              value={targetType}
              onChange={v => setTargetType(v as 'new' | 'existing')}
              style={{ marginTop: 8 }}
            />
          </SettingRow>

          {targetType === 'new' && (
            <SettingRow
              name={t['com.streamlain.import.preview.document-name']()}
              spreadCol={false}
            >
              <Input
                value={newDocName}
                onChange={setNewDocName}
                placeholder={t[
                  'com.streamlain.import.preview.document.placeholder'
                ]()}
                style={{ marginTop: 8, width: '100%' }}
              />
            </SettingRow>
          )}
        </SettingWrapper>
      </div>

      <div className={styles.footer}>
        <Button onClick={onCancel} disabled={importing}>
          {t['Cancel']()}
        </Button>
        <Button
          variant="primary"
          onClick={handleImport}
          disabled={importing || (targetType === 'new' && !newDocName.trim())}
        >
          {importing
            ? t['com.streamlain.import.preview.action.importing']()
            : t['Import']()}
        </Button>
      </div>
    </div>
  );
};
